import React, { useState, useEffect } from 'react';
import dolibarrApi from '../services/dolibarrApi';
import { useCart } from '../contexts/CartContext';
import SEO from './SEO';

const DOLIBARR_BASE_URL = 'https://accounts.firehousespirits.store/api/index.php';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const { addItem, getItemCount } = useCart();

  const loadingMessages = [
    'Pouring the bottles...',
    'Distilling that smoky flavour...',
    'Aging in oak barrels...',
    'Bottling the fire...',
    'Ready to ignite your taste buds...'
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (loading) {
      // Start with a random message
      setMessageIndex(Math.floor(Math.random() * loadingMessages.length));
      // Then cycle through messages every 2 seconds
      const interval = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [loading, loadingMessages.length]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await dolibarrApi.getProducts();
      setProducts(data);
    } catch (err) {
      setError('Failed to load products. Please try again later.');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    if (product.stock_reel > 0) {
      addItem(product);
    }
  };

  const getStockStatus = (stock) => {
    if (stock > 8) return { text: 'In Stock', class: 'stock-in-stock' };
    if (stock > 0) return { text: 'Limited Stock', class: 'stock-low-stock' };
    return { text: 'Out of Stock', class: 'stock-out-stock' };
  };

  if (loading) {
    return (
      <section className="shop">
        <h2>SHOP</h2>
        <div className="loading" style={{ fontSize: '1.5em', fontWeight: 'bold', textAlign: 'center', padding: '2em' }}>
          {loadingMessages[messageIndex]}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="shop">
        <h2>PERUSE THE COLLECTION</h2>
        <div className="error">{error}</div>
        <button onClick={fetchProducts} className="btn">Retry</button>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Shop Our Premium Spirits"
        description={`Browse our collection of ${products.length} premium craft spirits. Fire Engin Gin, Smokey Timber Rum, Bonfire Toffee Vodka and more. Free UK delivery available.`}
        url="/shop"
        type="product"
      />
      <section className="shop">
        <h2>PERUSE THE COLLECTION</h2>
        <div className="cart-indicator">
          Cart: {getItemCount()} items
        </div>
        <div className="products-grid">
          {products.map((product) => {
            const stockStatus = getStockStatus(product.stock_reel);
            return (
              <div key={product.id} className="product-card">
                <SEO
                  title={product.label}
                  description={product.description.replace(/<[^>]*>/g, '').substring(0, 155)}
                  image={product.image}
                  url={`/shop#product-${product.id}`}
                  type="product"
                  product={product}
                />
                <div className="product-image">
                  {product.image ? (
                    <img
                      src={product.image.startsWith('http') ? product.image : `https://accounts.firehousespirits.store${product.image}`}
                      alt={product.label}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <div className="no-image" style={{ display: product.image ? 'none' : 'block' }}>
                    No Image
                  </div>
                </div>
                <div className="product-info">
                  <h3>{product.label}</h3>
                  <div
                    className="product-description"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                  <div className="product-price">
                    £{parseFloat(product.price_ttc).toFixed(2)}
                  </div>
                  <div className={`stock-status ${stockStatus.class}`}>
                    {stockStatus.text} ({product.stock_reel} available)
                  </div>
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock_reel <= 0}
                    className="btn add-to-cart-btn"
                  >
                    {product.stock_reel > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default Shop;
